/*
 * /admin/operations/mail-templates — list, preview, and test-send the
 * @tallystream/mail bundled transactional templates. Drives operator
 * confidence: "what does the welcome email actually look like?" without
 * waiting for a real signup.
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../../lib/api.js";

type TemplateSummary = {
  id: string;
  label: string;
  description: string;
  sample: Record<string, unknown>;
};

type RenderedTemplate = {
  id: string;
  label: string;
  subject: string;
  text: string;
  html?: string;
};

export default function AdminMailTemplates() {
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [varsJson, setVarsJson] = useState<string>("");
  const [rendered, setRendered] = useState<RenderedTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState<"text" | "html">("text");
  const [testTo, setTestTo] = useState<string>("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    api.listMailTemplates()
      .then((r) => {
        setTemplates(r.templates);
        if (r.templates.length > 0 && !activeId) {
          setActiveId(r.templates[0]!.id);
          setVarsJson(JSON.stringify(r.templates[0]!.sample, null, 2));
        }
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-render the preview whenever the active template or vars change.
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    let parsedVars: Record<string, unknown> | undefined;
    try {
      if (varsJson.trim()) parsedVars = JSON.parse(varsJson);
    } catch {
      // Hold the previous render — let the operator finish editing JSON.
      return;
    }
    api.previewMailTemplate(activeId, parsedVars)
      .then((r) => { if (!cancelled) { setRendered(r); setError(null); } })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [activeId, varsJson]);

  function pickTemplate(id: string) {
    setActiveId(id);
    const t = templates?.find((x) => x.id === id);
    if (t) setVarsJson(JSON.stringify(t.sample, null, 2));
    setTestResult(null);
  }

  async function sendTest() {
    if (!activeId || !testTo) return;
    setTestBusy(true); setTestResult(null);
    let parsedVars: Record<string, unknown> | undefined;
    try {
      if (varsJson.trim()) parsedVars = JSON.parse(varsJson);
    } catch (e) {
      setTestResult({ ok: false, message: "vars JSON is invalid; fix it before sending." });
      setTestBusy(false);
      return;
    }
    try {
      const r = await api.sendTestMailTemplate(activeId, testTo, parsedVars);
      setTestResult({
        ok: true,
        message: `Sent via ${r.transport}${r.status === "logged" ? " (logged-only — SMTP not configured)" : ""}`
      });
    } catch (e) {
      setTestResult({ ok: false, message: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setTestBusy(false);
    }
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Mail templates</h1>
          <p className="muted">
            Preview the transactional templates shipped with TallyStream's in-built mail service
            (<code>@tallystream/mail</code>). Edit the variables panel to see how the same template
            renders for a different tenant. Send a test copy to a real address to verify SMTP
            delivery — falls back to a logged-only send when SMTP isn't configured under{" "}
            <a href="/admin/operations/smtp">SMTP settings</a>.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      {!templates && <p className="muted">Loading…</p>}

      {templates && (
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16 }}>
          {/* Left: template list */}
          <aside className="empty-state-card" style={{ padding: 0, alignSelf: "start" }}>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {templates.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => pickTemplate(t.id)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                      background: t.id === activeId ? "var(--bg-elevated, #eef)" : "transparent",
                      borderBottom: "1px solid var(--border, #ddd)",
                      cursor: "pointer"
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{t.label}</div>
                    <code style={{ fontSize: 11, color: "var(--text-muted)" }}>{t.id}</code>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Right: editor + preview + test */}
          <div style={{ display: "grid", gap: 16 }}>
            <section className="empty-state-card">
              <h2 style={{ marginTop: 0 }}>Variables (JSON)</h2>
              <textarea
                value={varsJson}
                onChange={(e) => setVarsJson(e.target.value)}
                rows={Math.min(16, varsJson.split("\n").length + 2)}
                style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }}
                spellCheck={false}
              />
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Edit live — the preview re-renders as you type. Invalid JSON is held until you fix it.
              </p>
            </section>

            <section className="empty-state-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ marginTop: 0 }}>Preview</h2>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    className={previewMode === "text" ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"}
                    onClick={() => setPreviewMode("text")}
                  >Text</button>
                  <button
                    type="button"
                    className={previewMode === "html" ? "btn btn-primary btn-sm" : "btn btn-ghost btn-sm"}
                    onClick={() => setPreviewMode("html")}
                    disabled={!rendered?.html}
                    title={rendered?.html ? "Render the HTML body" : "This template has no HTML body"}
                  >HTML</button>
                </div>
              </div>
              {!rendered && <p className="muted">No preview yet.</p>}
              {rendered && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <strong>Subject:</strong> {rendered.subject}
                  </div>
                  {previewMode === "text" && (
                    <pre style={{ background: "var(--bg-elevated, #f4f4f4)", padding: 12, fontSize: 12, overflow: "auto" }}>{rendered.text}</pre>
                  )}
                  {previewMode === "html" && rendered.html && (
                    <iframe
                      title={`${rendered.id}-html-preview`}
                      sandbox=""
                      srcDoc={rendered.html}
                      style={{ width: "100%", minHeight: 300, border: "1px solid var(--border, #ddd)", borderRadius: 4 }}
                    />
                  )}
                </>
              )}
            </section>

            <section className="empty-state-card">
              <h2 style={{ marginTop: 0 }}>Send test</h2>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <label className="transfer-filter" style={{ flex: "1 1 280px" }}>
                  Recipient
                  <input
                    type="email"
                    placeholder="ops@example.com"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!testTo || testBusy || !activeId}
                  onClick={() => void sendTest()}
                >{testBusy ? "Sending…" : "Send test"}</button>
              </div>
              {testResult && (
                <div className={testResult.ok ? "ts-alert ts-alert-info" : "ts-alert ts-alert-error"} style={{ marginTop: 8 }}>
                  {testResult.message}
                </div>
              )}
              <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                Uses the variables panel above. SMTP credentials come from{" "}
                <a href="/admin/operations/smtp">SMTP settings</a>; if none are configured, the
                send is logged to the API process stderr instead of bouncing real mail.
              </p>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
